/**
 * UsersTab — list all users in the admin's org scope.
 * Clicking a user navigates to the full-page UserEditPage wizard.
 */
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Badge, ActionIcon, Group, Text, TextInput, Loader,
  Alert, Tooltip, Stack,
} from '@mantine/core';
import { MdEdit, MdPersonOff, MdPersonAddAlt1, MdSearch, MdRefresh } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { getUsers, toggleUser } from '../../API/adminAPI';

const roleBadge = {
  atoms_admin: { color: 'violet', label: 'ATOMS Admin' },
  oem_admin: { color: 'blue', label: 'OEM Admin' },
  distributor_admin: { color: 'teal', label: 'Dist. Admin' },
  user: { color: 'gray', label: 'User' },
};

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getUsers();
      setUsers(data.users || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggle = async (userId, e) => {
    e.stopPropagation();
    try {
      await toggleUser(userId);
      load();
    } catch (e) {
      alert(e?.response?.data?.error || 'Toggle failed');
    }
  };

  const openCreate = () => navigate('/app/users/new');
  const openEdit = (user) => navigate(`/app/users/${user.id}/edit`, { state: { user } });

  const filtered = users.filter(
    (u) =>
      u.username.toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Loader mt="xl" mx="auto" />;

  return (
    <Stack>
      {error && <Alert color="red">{error}</Alert>}

      <Group position="apart">
        <TextInput
          placeholder="Search users…"
          icon={<MdSearch />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          sx={{ flex: 1, maxWidth: 320 }}
        />
        <Group spacing="xs">
          <ActionIcon onClick={load} variant="subtle" title="Refresh">
            <MdRefresh size={20} />
          </ActionIcon>
          <Button leftIcon={<MdPersonAddAlt1 />} onClick={openCreate}>
            New User
          </Button>
        </Group>
      </Group>

      <Table striped highlightOnHover>
        <thead>
          <tr>
            <th>Username</th>
            <th>Email</th>
            <th>Role</th>
            <th>Organisation</th>
            <th>Status</th>
            <th style={{ width: 90 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={6}>
                <Text color="dimmed" align="center" py="md">
                  No users found
                </Text>
              </td>
            </tr>
          ) : (
            filtered.map((u) => {
              const rb = roleBadge[u.role] || roleBadge.user;
              return (
                <tr
                  key={u.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => openEdit(u)}
                >
                  <td>
                    <Text size="sm" weight={500}>
                      {u.username}
                    </Text>
                  </td>
                  <td>
                    <Text size="sm" color="dimmed">
                      {u.email || '—'}
                    </Text>
                  </td>
                  <td>
                    <Badge color={rb.color} variant="light" size="sm">
                      {rb.label}
                    </Badge>
                  </td>
                  <td>
                    <Text size="sm">{u.organisation || '—'}</Text>
                  </td>
                  <td>
                    <Badge
                      color={u.is_active ? 'green' : 'red'}
                      variant="dot"
                      size="sm"
                    >
                      {u.is_active ? 'Active' : 'Suspended'}
                    </Badge>
                  </td>
                  <td>
                    <Group spacing={4} onClick={(e) => e.stopPropagation()}>
                      <Tooltip label="Edit user">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          onClick={() => openEdit(u)}
                        >
                          <MdEdit />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={u.is_active ? 'Suspend user' : 'Activate user'}
                      >
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color={u.is_active ? 'orange' : 'green'}
                          onClick={(e) => handleToggle(u.id, e)}
                        >
                          <MdPersonOff />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>
    </Stack>
  );
};

export default UsersTab;
